import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStoreContext } from 'store/app';
import { useTranslationsContext } from 'store/translations';
import { Modal, ModalProps } from '../modal';
import { IPrizeBlockConfig, prizes, superprizeTickets } from './config';
import cn from 'classnames';

import Ticket from 'assets/img/ticket.png';

import item1 from 'assets/img/lotteryRewards/item_1.png';
import item2 from 'assets/img/lotteryRewards/item_2.png';
import item4 from 'assets/img/lotteryRewards/item_4.png';

import { AllPrizesModal } from './allPrizesModal';
import './drawModal.styl';
import Label from 'components/core/Label';
import { sanitize } from 'dompurify';

interface DrawModalProps extends ModalProps {}

export const DrawModal = observer((props: DrawModalProps) => {
    const { translate } = useTranslationsContext();
    const { userState, logOperation, isMainPartFinished } = useStoreContext();
    const [openAllPrizesModal, setOpenAllPrizesModal] = useState(false);

    useEffect(() => {
        logOperation('raffle', 'render');
    }, []);

    const ticketsCount = userState?.tickets?.length ?? 0;

    const allPrizesButton = (
        <div className="draw-modal-all-prizes" onClick={() => setOpenAllPrizesModal(true)}>
            <div className="draw-modal-all-prizes-text">{translate('all.prizes')}</div>
            <img className="draw-modal-all-prizes-image headphones" src={item1}></img>
            <img className="draw-modal-all-prizes-image coffemaker" src={item4}></img>
            <img className="draw-modal-all-prizes-image watch" src={item2}></img>
        </div>
    );

    const renderPrizeBlock = (prize: IPrizeBlockConfig) => {
        const { minTickets, maxTickets, descKey, items } = prize;

        return (
            <div className={cn('draw-modal-prize', { active: ticketsCount > 0 })} key={minTickets}>
                <div className="draw-modal-prize-title">
                    {translate('draw.prize.title', {
                        count: minTickets === maxTickets ? minTickets : `${minTickets} - ${maxTickets}`,
                        countPlural: maxTickets
                    })}
                </div>
                <div className="draw-modal-prize-desc">
                    {translate(isMainPartFinished ? 'prize.end.desc' : descKey)}
                </div>
                {items.map((item) => {
                    const { itemNameKey, count, imageUrl, id } = item;

                    return (
                        <div className="draw-modal-prize-info" key={id}>
                            <img className="draw-modal-prize-info-image" src={imageUrl}></img>
                            <div className="draw-modal-prize-info-content">
                                <div
                                    className="draw-modal-prize-info-shortname"
                                    dangerouslySetInnerHTML={{
                                        __html: sanitize(
                                            translate(itemNameKey) + ' — ' + translate('snake.amount.draw', { count })
                                        )
                                    }}
                                ></div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const superPrizeBlock = (
        <div className="draw-modal-super-prize">
            <div className="draw-modal-super-prize-title">
                {translate('draw.prize.title', {
                    count: superprizeTickets,
                    countPlural: superprizeTickets
                })}
            </div>
            <div className="draw-modal-super-prize-desc">
                {translate(isMainPartFinished ? 'prize.end.desc' : 'superprize.desc')}
            </div>
            <div className="draw-modal-super-prize-amount">
                <Label isRawHtml of="superprize.amount" />
            </div>
            <div className="draw-modal-super-prize-count">{translate('superprize.count', { count: 10 })}</div>
        </div>
    );

    const getActivePrize = () => {
        const prize = prizes.find((x) => ticketsCount <= x.maxTickets && ticketsCount >= x.minTickets);

        if (prize) {
            return renderPrizeBlock(prize);
        }

        return superPrizeBlock;
    };

    return (
        <>
            <Modal
                title={translate(isMainPartFinished ? 'draw.title.finished' : 'draw.title')}
                className="draw"
                {...props}
            >
                <div className="draw-modal">
                    <div className="draw-modal-heading">
                        {translate(isMainPartFinished ? 'draw.heading.finished' : 'draw.heading')}
                    </div>
                    {!isMainPartFinished || ticketsCount === 0 ? (
                        <div className="draw-modal-counter">
                            <img className="draw-modal-counter-image" src={Ticket}></img>
                            <span className="draw-modal-counter-text">
                                {ticketsCount
                                    ? translate('draw.counter', { count: ticketsCount, countPlural: ticketsCount })
                                    : translate('draw.counter.zero')}
                            </span>
                        </div>
                    ) : (
                        <div className="draw-modal-counter-end">
                            <div className="draw-modal-counter-end-title">
                                {translate('your.tickets')}
                                <img className="draw-modal-counter-end-image" src={Ticket}></img>
                            </div>
                            <span className="draw-modal-counter-end-tickets">
                                {userState?.tickets?.map((x) => x.ticket_id).join(', ')}
                            </span>
                        </div>
                    )}
                    {ticketsCount !== 0 ? getActivePrize() : null}
                    {!isMainPartFinished && allPrizesButton}
                    {ticketsCount === 0 ? (
                        <>
                            {prizes.map(renderPrizeBlock)}
                            {superPrizeBlock}
                        </>
                    ) : null}
                    {!isMainPartFinished && <div className="draw-modal-hint">{translate('prizes.hint')}</div>}
                </div>
            </Modal>
            <AllPrizesModal isOpened={openAllPrizesModal} onClose={() => setOpenAllPrizesModal(false)} />
        </>
    );
});
